<template>
  <div
    class="modal fade"
    id="userCreateModal"
    tabindex="-1"
    aria-labelledby="userCreateModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content border-0 shadow">
        <div class="modal-header border-bottom-dashed">
          <div>
            <span class="modal-chip">{{ user ? $t("user_page.edit_user_chip") : $t("user_page.new_user") }}</span>
            <h5 class="modal-title fw-bold mt-3 mb-1" id="userCreateModalLabel">
              {{ user ? $t("user_page.update_user") : $t("user_page.create_user") }}
            </h5>
            <p class="text-muted mb-0">
              {{ $t("user_page.modal_description") }}
            </p>
          </div>
          <button
            type="button"
            class="btn-close shadow-none"
            data-bs-dismiss="modal"
            :aria-label="$t('common.close')"
          ></button>
        </div>

        <div class="modal-body p-4 p-xl-40">
          <form @submit.prevent="handleSubmit">
            <div class="row g-3">
              <div class="col-12">
                <div class="avatar-panel">
                  <div class="avatar-preview-wrap">
                    <img :src="previewAvatarUrl" :alt="$t('user_page.avatar_preview')" class="avatar-preview" />
                  </div>
                  <div class="avatar-meta">
                    <div class="field-header mb-2">
                      <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.profile_image") }}</label>
                      <span class="field-caption">{{ $t("user_page.profile_image_caption") }}</span>
                    </div>
                    <input
                      ref="avatarInput"
                      type="file"
                      class="d-none"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      @change="handleAvatarChange"
                    />
                    <div class="d-flex flex-wrap gap-2">
                      <button type="button" class="btn btn-light px-4 h-45" @click="openAvatarPicker">
                        {{ $t("user_page.choose_image") }}
                      </button>
                      <button
                        v-if="selectedAvatarName"
                        type="button"
                        class="btn btn-outline-secondary px-4 h-45"
                        @click="clearSelectedAvatar"
                      >
                        {{ $t("user_page.remove_image") }}
                      </button>
                    </div>
                    <small v-if="selectedAvatarName" class="text-muted d-block mt-2">
                      {{ $t("user_page.selected_file", { name: selectedAvatarName }) }}
                    </small>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.first_name") }}</label>
                  <span class="field-caption">{{ $t("user_page.first_name_caption") }}</span>
                </div>
                <input
                  v-model="formData.first_name"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.first_name }"
                  :placeholder="$t('user_page.first_name_placeholder')"
                  required
                />
                <div v-if="fieldErrors.first_name" class="invalid-feedback">{{ fieldErrors.first_name }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.last_name") }}</label>
                  <span class="field-caption">{{ $t("user_page.last_name_caption") }}</span>
                </div>
                <input
                  v-model="formData.last_name"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.last_name }"
                  :placeholder="$t('user_page.last_name_placeholder')"
                  required
                />
                <div v-if="fieldErrors.last_name" class="invalid-feedback">{{ fieldErrors.last_name }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.username") }}</label>
                  <span class="field-caption">{{ $t("user_page.username_caption") }}</span>
                </div>
                <input
                  v-model="formData.username"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.username }"
                  :placeholder="$t('user_page.username_placeholder')"
                  required
                />
                <div v-if="fieldErrors.username" class="invalid-feedback">{{ fieldErrors.username }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.email") }}</label>
                  <span class="field-caption">{{ $t("user_page.email_caption") }}</span>
                </div>
                <input
                  v-model="formData.email"
                  type="email"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.email }"
                  :placeholder="$t('user_page.email_placeholder')"
                  required
                />
                <div v-if="fieldErrors.email" class="invalid-feedback">{{ fieldErrors.email }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.phone") }}</label>
                  <span class="field-caption">{{ $t("user_page.phone_caption") }}</span>
                </div>
                <input
                  v-model="formData.phone"
                  type="text"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.phone }"
                  :placeholder="$t('user_page.phone_placeholder')"
                  required
                />
                <div v-if="fieldErrors.phone" class="invalid-feedback">{{ fieldErrors.phone }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.password") }}</label>
                  <span class="field-caption">{{ user ? $t("user_page.password_caption_edit") : $t("user_page.password_caption_create") }}</span>
                </div>
                <input
                  v-model="formData.password"
                  type="password"
                  class="form-control modal-input"
                  :class="{ 'is-invalid': fieldErrors.password }"
                  :placeholder="$t('user_page.password_placeholder')"
                  :required="!user"
                />
                <div v-if="fieldErrors.password" class="invalid-feedback">{{ fieldErrors.password }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.warehouse") }}</label>
                  <span class="field-caption">{{ $t("user_page.warehouse_caption") }}</span>
                </div>
                <select v-model="formData.warehouse_id" class="form-select modal-input">
                  <option :value="null">{{ $t("common.none") }}</option>
                  <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
                    {{ warehouse.name }}
                  </option>
                </select>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.branch") }}</label>
                  <span class="field-caption">{{ $t("user_page.branch_caption") }}</span>
                </div>
                <select v-model="formData.branch_id" class="form-select modal-input">
                  <option :value="null">{{ $t("common.none") }}</option>
                  <option v-for="branch in branches" :key="branch.id" :value="branch.id">
                    {{ branch.name }}
                  </option>
                </select>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.biller") }}</label>
                  <span class="field-caption">{{ $t("user_page.biller_caption") }}</span>
                </div>
                <select v-model="formData.biller_id" class="form-select modal-input">
                  <option :value="null">{{ $t("common.none") }}</option>
                  <option v-for="biller in billers" :key="biller.id" :value="biller.id">
                    {{ biller.name }}
                  </option>
                </select>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.role_permission") }}</label>
                  <span class="field-caption">{{ $t("user_page.role_permission_caption") }}</span>
                </div>
                <select
                  v-model="formData.role_permission_id"
                  class="form-select modal-input"
                  :class="{ 'is-invalid': fieldErrors.role_permission_id }"
                >
                  <option :value="null">{{ $t("user_page.select_role") }}</option>
                  <option v-for="role in roles" :key="role.id" :value="role.id">
                    {{ role.role_name }}
                  </option>
                </select>
                <div v-if="fieldErrors.role_permission_id" class="invalid-feedback">{{ fieldErrors.role_permission_id }}</div>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.gender") }}</label>
                  <span class="field-caption">{{ $t("user_page.gender_caption") }}</span>
                </div>
                <select v-model="formData.gender" class="form-select modal-input">
                  <option :value="1">{{ $t("user_page.gender_male") }}</option>
                  <option :value="0">{{ $t("user_page.gender_female") }}</option>
                </select>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.status") }}</label>
                  <span class="field-caption">{{ $t("user_page.status_caption") }}</span>
                </div>
                <select v-model="formData.active" class="form-select modal-input">
                  <option :value="1">{{ $t("user_page.status_active") }}</option>
                  <option :value="0">{{ $t("user_page.status_inactive") }}</option>
                </select>
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.company") }}</label>
                  <span class="field-caption">{{ $t("user_page.company_caption") }}</span>
                </div>
                <input
                  v-model="formData.company"
                  type="text"
                  class="form-control modal-input"
                  :placeholder="$t('user_page.company_placeholder')"
                />
              </div>

              <div class="col-md-6">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.pin_code") }}</label>
                  <span class="field-caption">{{ $t("user_page.pin_code_caption") }}</span>
                </div>
                <input
                  v-model="formData.pin_code"
                  type="password"
                  class="form-control modal-input"
                  :placeholder="$t('user_page.pin_code_placeholder')"
                />
              </div>

              <div class="col-12">
                <div class="field-header">
                  <label class="form-label fw-semibold text-title mb-0">{{ $t("user_page.address") }}</label>
                  <span class="field-caption">{{ $t("user_page.address_caption") }}</span>
                </div>
                <textarea
                  v-model="formData.address"
                  rows="4"
                  class="form-control modal-input modal-textarea"
                  :placeholder="$t('user_page.address_placeholder')"
                ></textarea>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-4 pt-2">
              <button type="button" class="btn btn-light px-4" data-bs-dismiss="modal">
                {{ $t("common.cancel") }}
              </button>
              <button type="submit" class="btn style-one px-4 h-55" :disabled="loading">
                {{ loading ? $t("user_page.saving") : user ? $t("user_page.update_user") : $t("user_page.create_user") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, PropType, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import userService, { type User } from "../../../services/user";
import type { RolePermission } from "../../../services/role_permission";
import type { Warehouse } from "../../../services/warehouse";
import type { Branch } from "../../../services/branch";
import type { Biller } from "../../../services/biller";
import defaultAvatarImg from "../../../assets/img/users/user-default.jpg";
import { buildAssetUrl, buildStorageAssetUrl } from "../../../utils/settings";

type UserFormState = {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  phone: string;
  password: string;
  active: number;
  role_permission_id?: number | null;
  warehouse_id?: number | null;
  branch_id?: number | null;
  biller_id?: number | null;
  gender: number;
  address: string;
  company: string;
  pin_code: string;
  view_right: number;
  edit_right: number;
  status_deleted: number;
};

export default defineComponent({
  name: "CreateUserModal",
  props: {
    user: {
      type: Object as PropType<User | null>,
      default: null,
    },
    roles: {
      type: Array as PropType<RolePermission[]>,
      default: () => [],
    },
    warehouses: {
      type: Array as PropType<Warehouse[]>,
      default: () => [],
    },
    branches: {
      type: Array as PropType<Branch[]>,
      default: () => [],
    },
    billers: {
      type: Array as PropType<Biller[]>,
      default: () => [],
    },
  },
  emits: ["saved"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const loading = ref(false);
    const avatarInput = ref<HTMLInputElement | null>(null);
    const selectedAvatarFile = ref<File | null>(null);
    const previewObjectUrl = ref<string | null>(null);
    const fieldErrors = reactive<Record<string, string>>({});
    const formData = reactive<UserFormState>({
      first_name: "",
      last_name: "",
      username: "",
      email: "",
      phone: "",
      password: "",
      active: 1,
      role_permission_id: null,
      warehouse_id: null,
      branch_id: null,
      biller_id: null,
      gender: 1,
      address: "",
      company: "",
      pin_code: "",
      view_right: 1,
      edit_right: 1,
      status_deleted: 0,
    });

    const resetForm = () => {
      formData.first_name = "";
      formData.last_name = "";
      formData.username = "";
      formData.email = "";
      formData.phone = "";
      formData.password = "";
      formData.active = 1;
      formData.role_permission_id = null;
      formData.warehouse_id = null;
      formData.branch_id = null;
      formData.biller_id = null;
      formData.gender = 1;
      formData.address = "";
      formData.company = "";
      formData.pin_code = "";
      formData.view_right = 1;
      formData.edit_right = 1;
      formData.status_deleted = 0;
      clearSelectedAvatar();
    };

    const avatarUrl = computed(() => {
      const avatar = props.user?.avatar;
      if (!avatar) return defaultAvatarImg;
      if (String(avatar).startsWith("http")) return String(avatar);

      if (String(avatar).startsWith("uploads/")) {
        return buildAssetUrl(String(avatar));
      }

      return buildStorageAssetUrl(String(avatar));
    });

    const previewAvatarUrl = computed(() => previewObjectUrl.value || avatarUrl.value);
    const selectedAvatarName = computed(() => selectedAvatarFile.value?.name || "");

    const clearPreviewObjectUrl = () => {
      if (previewObjectUrl.value) {
        URL.revokeObjectURL(previewObjectUrl.value);
        previewObjectUrl.value = null;
      }
    };

    const openAvatarPicker = () => {
      avatarInput.value?.click();
    };

    const handleAvatarChange = (event: Event) => {
      const target = event.target as HTMLInputElement | null;
      const file = target?.files?.[0] || null;

      clearPreviewObjectUrl();
      selectedAvatarFile.value = file;

      if (file) {
        previewObjectUrl.value = URL.createObjectURL(file);
      }
    };

    const clearSelectedAvatar = () => {
      selectedAvatarFile.value = null;
      clearPreviewObjectUrl();

      if (avatarInput.value) {
        avatarInput.value.value = "";
      }
    };

    watch(
      () => props.user,
      (newUser) => {
        Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);
        clearSelectedAvatar();

        if (newUser) {
          formData.first_name = newUser.first_name || "";
          formData.last_name = newUser.last_name || "";
          formData.username = newUser.username || "";
          formData.email = newUser.email || "";
          formData.phone = newUser.phone || "";
          formData.password = "";
          formData.active =
            newUser.active !== undefined
              ? Number(newUser.active ? 1 : 0)
              : newUser.status === "active"
                ? 1
                : 0;
          formData.role_permission_id = newUser.role_permission_id;
          formData.warehouse_id = newUser.warehouse_id;
          formData.branch_id = newUser.branch_id;
          formData.biller_id = newUser.biller_id;
          formData.gender =
            newUser.gender !== undefined ? Number(newUser.gender ? 1 : 0) : 1;
          formData.address = newUser.address || "";
          formData.company = newUser.company || "";
          formData.pin_code = "";
          formData.view_right =
            newUser.view_right !== undefined ? Number(newUser.view_right ? 1 : 0) : 1;
          formData.edit_right =
            newUser.edit_right !== undefined ? Number(newUser.edit_right ? 1 : 0) : 1;
          formData.status_deleted = Number(newUser.status_deleted || 0);
          return;
        }

        resetForm();
      },
      { immediate: true }
    );

    const hideModal = () => {
      const modalElement = document.getElementById("userCreateModal");
      if (!modalElement) return;

      const dismissButton = modalElement.querySelector(
        '[data-bs-dismiss="modal"]'
      ) as HTMLButtonElement | null;
      if (dismissButton) {
        dismissButton.click();
      }

      const bootstrap = (window as {
        bootstrap?: {
          Modal?: {
            getInstance: (el: Element) => { hide: () => void } | null;
            getOrCreateInstance?: (el: Element) => { hide: () => void };
          };
        };
      }).bootstrap;
      const modalInstance =
        bootstrap?.Modal &&
        (bootstrap.Modal.getInstance(modalElement) ||
          bootstrap.Modal.getOrCreateInstance?.(modalElement));
      modalInstance?.hide();

      modalElement.classList.remove("show");
      modalElement.setAttribute("aria-hidden", "true");
      modalElement.removeAttribute("aria-modal");
      modalElement.removeAttribute("role");
      (modalElement as HTMLElement).style.display = "none";
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("padding-right");
      document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
    };

    const handleSubmit = async () => {
      loading.value = true;
      Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);

      try {
        const payload = new FormData();
        payload.append("first_name", formData.first_name);
        payload.append("last_name", formData.last_name);
        payload.append("username", formData.username);
        payload.append("email", formData.email);
        payload.append("phone", formData.phone);
        payload.append("active", String(formData.active));
        payload.append("gender", String(formData.gender));
        payload.append("address", formData.address);
        payload.append("company", formData.company);
        payload.append("pin_code", formData.pin_code);
        payload.append("view_right", String(formData.view_right));
        payload.append("edit_right", String(formData.edit_right));
        payload.append("status_deleted", String(formData.status_deleted));

        if (formData.password) {
          payload.append("password", formData.password);
        }
        if (formData.role_permission_id !== null && formData.role_permission_id !== undefined) {
          payload.append("role_permission_id", String(formData.role_permission_id));
        }
        payload.append(
          "warehouse_id",
          formData.warehouse_id === null || formData.warehouse_id === undefined
            ? ""
            : String(formData.warehouse_id)
        );
        payload.append(
          "branch_id",
          formData.branch_id === null || formData.branch_id === undefined
            ? ""
            : String(formData.branch_id)
        );
        payload.append(
          "biller_id",
          formData.biller_id === null || formData.biller_id === undefined
            ? ""
            : String(formData.biller_id)
        );
        if (selectedAvatarFile.value) {
          payload.append("avatar", selectedAvatarFile.value);
        }

        if (props.user?.id) {
          await userService.updateUser(props.user.id, payload);
        } else {
          await userService.createUser(payload);
        }

        hideModal();
        resetForm();
        await nextTick();
        emit("saved");

        await Swal.fire({
          icon: "success",
          title: props.user?.id ? t("user_page.user_updated") : t("user_page.user_created"),
          text: t("user_page.user_saved_successfully"),
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error: unknown) {
        console.error("Failed to save user:", error);
        const axiosError = error as {
          response?: { data?: { errors?: Record<string, string[] | string>; message?: string } };
        };

        if (axiosError.response?.data?.errors) {
          const errors = axiosError.response.data.errors;
          Object.keys(errors).forEach((key) => {
            const fieldValue = errors[key];
            fieldErrors[key] = Array.isArray(fieldValue) ? String(fieldValue[0] || "") : String(fieldValue || "");
          });
        } else {
          await Swal.fire({
            icon: "error",
            title: t("user_page.save_failed"),
            text: axiosError.response?.data?.message || t("user_page.unable_to_save_user"),
          });
        }
      } finally {
        loading.value = false;
      }
    };

    onBeforeUnmount(() => {
      clearPreviewObjectUrl();
    });

    return {
      formData,
      fieldErrors,
      handleSubmit,
      loading,
      avatarInput,
      previewAvatarUrl,
      selectedAvatarName,
      openAvatarPicker,
      handleAvatarChange,
      clearSelectedAvatar,
    };
  },
});
</script>

<style scoped>
.modal-content {
  border-radius: 20px;
}

.modal-chip {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #edf2ff;
  color: #5d5fef;
  font-weight: 700;
  font-size: 13px;
}

.border-bottom-dashed {
  border-bottom: 1px dashed #e7ebf3;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.field-caption {
  font-size: 12px;
  color: #98a2b3;
}

.modal-input {
  min-height: 52px;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  background: #fbfcfe;
  color: #1f2940;
  box-shadow: none;
  padding: 12px 16px;
}

.modal-input:focus {
  border-color: #5d5fef;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(93, 95, 239, 0.12);
}

.modal-textarea {
  min-height: 140px;
  resize: vertical;
}

.avatar-panel {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  border: 1px solid #e7ebf3;
  border-radius: 18px;
  background: #f8faff;
}

.avatar-preview {
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
}

.avatar-meta {
  flex: 1 1 auto;
}

.h-45 {
  height: 45px;
}

@media (max-width: 767.98px) {
  .avatar-panel {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
